/**
 * เปลือกนอกของทุกหน้า — แถบหัวเส้นเดียวกับรางความคืบหน้า (ตาม mockup)
 *
 * ★ โลโก้คือ **TEWTORPⁿ** = `TEWTOR` + `P` ยกกำลัง `n` (07-DOMAIN §1.5)
 *   - `TEWTOR` สี `--ink` ตัวพิมพ์ใหญ่ letter-spacing .10em
 *   - `P` สี `--accent` ใหญ่กว่าตัวอื่น และ **มีขีดเรืองแสงใต้ `P` เท่านั้น**
 *     ขีดจึงต้องเกาะ `.base` ไม่ใช่ `.p` (ไม่งั้นจะลากคลุม `ⁿ` ไปด้วย)
 *   - `n` เป็น `<sup>` ตัวพิมพ์เล็ก ไม่มีขีดใต้
 *   **ห้ามแบ่งคำเป็น TEW|TORP หรือ TEWT|ORP**
 *   ⚠️ **ห้ามแสดงคำอ่านไทยกำกับโลโก้ในหน้าใด ๆ และใน meta tag**
 *      คำอ่านทางการคือ "ติวเตอร์พี" แต่ใช้เฉพาะตอนพูด/สื่อสารภายนอกเท่านั้น
 *      ไม่ใช่ข้อความที่เรนเดอร์ออกจอ (07-DOMAIN §1)
 *   ⚠️ ที่มาของชื่อเป็นเรื่องส่วนตัวของเจ้าของ — บันทึกใน `07-DOMAIN.md` เท่านั้น
 *      **ห้ามใส่ในหน้าเว็บ หน้า About หรือ meta tag ใด ๆ**
 *
 * ปุ่ม "ออกก่อน" ต้องหาเจอง่ายและออกได้โดยไม่เสียงาน — เด็กถูกเรียกไปกินข้าว
 * กลางคันเป็นเรื่องปกติ งานถูกบันทึกลงเครื่องทุกครั้งที่ปล่อยปากกาอยู่แล้ว
 *
 * ★★ ทางออกของทุกหน้า (8 ก.ย. 2569)
 *   บน iPad ที่เปิดจากไอคอนหน้าจอโฮมไม่มีแถบเบราว์เซอร์และปัดกลับไม่ได้
 *   ทุกหน้าจึงต้องมีทางออกบนจอเอง ไม่ใช่พึ่งเบราว์เซอร์
 *
 *   - ปุ่มย้อนกลับอยู่ **มุมซ้ายบน** และเป็น **การนำทางจริง** ไปยัง `parent`
 *     ที่กำหนดไว้ใน `lib/routes.ts` — **ห้ามใช้ `history.back()`**
 *     เพราะเข้าตรงจาก URL จะไม่มีประวัติให้ย้อน หรือย้อนออกนอกแอปไปเลย
 *   - หน้าไหนมี `onQuit` (หน้าทำข้อสอบ) จะ **ไม่มี** ปุ่มย้อนกลับ
 *     หนึ่งหัวเว็บมีทางออกเดียว ไม่งั้นเด็กต้องเดาว่าสองปุ่มต่างกันยังไง
 *   - ปุ่มย้อนกลับอยู่คนละฝั่งกับปุ่มที่ทำลายข้อมูล และสูงอย่างน้อย 44px
 */
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { translate as t } from "@/i18n";
import { parentOf } from "@/lib/routes";

interface Props {
  /** 0–1 · null = ไม่แสดงราง */
  progress?: number | null;
  here?: string | undefined;
  onQuit?: (() => void) | undefined;
  /**
   * ล็อกการเลือกข้อความในหน้านี้ — ใช้กับหน้าที่มี **โจทย์หรือเฉลย** เท่านั้น
   *
   * ★ ไม่ใช่เรื่องความรำคาญ: เมนูกดค้างของ iOS มี "ค้นหาในเว็บ" กับ "แปลภาษา"
   *   เด็กลากนิ้วบนโจทย์แล้วกดปุ่มเดียวก็ออกไปหาคำตอบได้
   * ⚠️ ห้ามเปิดทั้งเว็บ — หน้าผู้ปกครองต้องเลือกคัดลอกรหัสครอบครัวได้
   */
  lockText?: boolean;
  /** D18 — ป้ายผู้เรียนมุมขวาของธีมหนังสือเรียน (ชั้น + ชื่อเล่น) · ไม่ส่ง = ไม่มี DOM เพิ่ม */
  who?: { label: string; name: string } | undefined;
  children: ReactNode;
}

export function Shell({
  progress = null,
  here,
  onQuit,
  lockText = false,
  who,
  children,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // ★ เส้นทางกลับมาจากตาราง ไม่ใช่จากประวัติของเบราว์เซอร์
  const back = onQuit ? null : parentOf(pathname);

  return (
    <>
      <header className={`top${lockText ? " no-select" : ""}`}>
        <div className="top-in">
          {back ? (
            <button
              type="button"
              className="back"
              data-exit="back"
              aria-label={t("nav.back")}
              onClick={() => navigate(back)}
            >
              <span aria-hidden="true">←</span>
              <span className="lbl">{t("nav.back")}</span>
            </button>
          ) : null}
          <div className="mark">
            {t("brand.mark")}
            <span className="p">
              <span className="base">{t("brand.markAccent")}</span>
              <sup>{t("brand.markSup")}</sup>
            </span>
          </div>
          {here ? <div className="here">{here}</div> : null}
          <div className="spacer" />
          {who ? (
            <div className="who" data-testid="who">
              <span className="av">{who.label}</span>
              {who.name}
            </div>
          ) : null}
          {onQuit ? (
            <button
              type="button"
              className="quit"
              data-exit="quit"
              onClick={onQuit}
            >
              {t("nav.quit")}
            </button>
          ) : null}
        </div>
        <div className="rail">
          <i
            style={{
              width: progress === null ? 0 : `${Math.round(progress * 100)}%`,
            }}
          />
        </div>
      </header>
      <div className={`wrap${lockText ? " no-select" : ""}`}>{children}</div>
    </>
  );
}

export function Center({ children }: { children: ReactNode }): JSX.Element {
  return <div className="center">{children}</div>;
}
